import { AbstractControl, ValidatorFn } from '@angular/forms';
import { FinancialProductEnum, TaxType } from '../../model/open-quote.model';

export class OpenQuoteValidations {
  private static isNoAplica(value: any): boolean {
    return value === undefined || value === null || value === '' || value === 'null';
  }

  private static isEmptyOrZero(value: any): boolean {
    return value === undefined || value === null || value === '' || value === 'null' || Number(value) === 0;
  }

  private static setControlError(control: any, key: string, shouldSet: boolean): void {
    if (!control) return;
    const current = control.errors || {};
    if (shouldSet) {
      if (!current[key]) control.setErrors({ ...current, [key]: true });
    } else if (current[key]) {
      const { [key]: _removed, ...rest } = current;
      control.setErrors(Object.keys(rest).length ? rest : null);
    }
  }

  /**
   * Valida que el tipo de depósito sea permitido para el producto y dentro del set de valores válidos.
   */
  static validateWarrantyDepositTypeAllowedForProduct(
    warrantyTypeControlName: string,
    financialProductTypeControlName: string
  ): ValidatorFn {
    return (formGroup: AbstractControl): { [key: string]: any } | null => {
      const typeControl = formGroup.get(warrantyTypeControlName);
      const productControl = formGroup.get(financialProductTypeControlName);
      if (!typeControl) return null;

      const typeValue = typeControl.value;
      const productType = productControl?.value;
      const allowedTypes = new Set([
        TaxType.numberOfRents,
        TaxType.invoiceWithTax,
        TaxType.invoiceWithoutTax,
        TaxType.fixedAmount,
      ]);

      const isNoAplica = this.isNoAplica(typeValue);
      const isAllowedType = allowedTypes.has(typeValue);

      let hasError = false;
      if (productType === FinancialProductEnum.pureLease && isNoAplica) {
        hasError = true;
      } else if (!isNoAplica && !isAllowedType) {
        hasError = true;
      }

      this.setControlError(typeControl, 'invalidWarrantyDepositType', hasError);
      return hasError ? { invalidWarrantyDepositType: true } : null;
    };
  }

  /**
   * Valida campos requeridos/permitidos según el tipo de depósito de garantía seleccionado.
   * Controla qué campos deben estar llenos o vacíos dependiendo del tipo de garantía:
   * - numberOfRents: requiere mes válido (>=0) y total válido (>=0), porcentaje debe estar vacío
   * - fixedAmount: requiere total válido (>=0), porcentaje y mes deben estar vacíos
   * - invoiceWithTax/invoiceWithoutTax: requiere porcentaje válido (>=0), mes y total deben estar vacíos
   * - No aplica: todos los campos deben estar vacíos
   */
  static validateWarrantyDepositFieldsByType(
    warrantyTypeControlName: string,
    percentageControlName: string,
    monthControlName: string,
    totalControlName: string
  ): ValidatorFn {
    return (formGroup: AbstractControl): { [key: string]: any } | null => {
      const typeControl = formGroup.get(warrantyTypeControlName);
      const percentageControl = formGroup.get(percentageControlName);
      const monthControl = formGroup.get(monthControlName);
      const totalControl = formGroup.get(totalControlName);
      if (!typeControl) return null;

      const typeValue = typeControl.value;
      const isNoAplica = this.isNoAplica(typeValue);
      let hasError = false;
      const totalVal = totalControl?.value;
      const totalMissing = totalControl && (totalVal === null || totalVal === undefined || totalVal === '');
      if (typeValue === TaxType.numberOfRents) {
        const monthVal = monthControl?.value;
        const monthMissing = monthControl && (monthVal === null || monthVal === undefined || monthVal === '');
        this.setControlError(monthControl, 'requiredByType', !!monthMissing);
        hasError = hasError || !!monthMissing;

        this.setControlError(percentageControl, 'mustBeEmptyByType', !(percentageControl && this.isEmptyOrZero(percentageControl.value)));
        hasError = hasError || !(percentageControl && this.isEmptyOrZero(percentageControl.value));

        this.setControlError(totalControl, 'requiredByType', !!totalMissing);
        hasError = hasError || !!totalMissing;
      } else if (typeValue === TaxType.fixedAmount) {
        this.setControlError(totalControl, 'requiredByType', !!totalMissing);
        hasError = hasError || !!totalMissing;

        this.setControlError(percentageControl, 'mustBeEmptyByType', !(percentageControl && this.isEmptyOrZero(percentageControl.value)));
        hasError = hasError || !(percentageControl && this.isEmptyOrZero(percentageControl.value));

        this.setControlError(monthControl, 'mustBeEmptyByType', !(monthControl && this.isEmptyOrZero(monthControl.value)));
        hasError = hasError || !(monthControl && this.isEmptyOrZero(monthControl.value));
      } else if (typeValue === TaxType.invoiceWithTax || typeValue === TaxType.invoiceWithoutTax) {
        const percVal = percentageControl?.value;
        const percentageMissing = percentageControl && (percVal === null || percVal === undefined || percVal === '');
        this.setControlError(percentageControl, 'requiredByType', !!percentageMissing);
        hasError = hasError || !!percentageMissing;

        this.setControlError(monthControl, 'mustBeEmptyByType', !(monthControl && this.isEmptyOrZero(monthControl.value)));
        hasError = hasError || !(monthControl && this.isEmptyOrZero(monthControl.value));

        this.setControlError(totalControl, 'requiredByType', !!totalMissing);
      } else if (isNoAplica) {
        this.setControlError(percentageControl, 'mustBeEmptyByType', !(percentageControl && this.isEmptyOrZero(percentageControl.value)));
        hasError = hasError || !(percentageControl && this.isEmptyOrZero(percentageControl.value));
        this.setControlError(monthControl, 'mustBeEmptyByType', !(monthControl && this.isEmptyOrZero(monthControl.value)));
        hasError = hasError || !(monthControl && this.isEmptyOrZero(monthControl.value));
        this.setControlError(totalControl, 'mustBeEmptyByType', !(totalControl && this.isEmptyOrZero(totalControl.value)));
        hasError = hasError || !(totalControl && this.isEmptyOrZero(totalControl.value));
      }

      return hasError ? { warrantyDepositInvalid: true } : null;
    };
  }

  /**
   * Wrapper de compatibilidad que ejecuta ambas validaciones anteriores.
   */
  static validateWarrantyDepositType(): ValidatorFn {
    return (formGroup: AbstractControl): { [key: string]: any } | null => {
      const a = this.validateWarrantyDepositTypeAllowedForProduct('warrantyDeposit', 'financialProductType')(formGroup);
      const b = this.validateWarrantyDepositFieldsByType('warrantyDeposit', 'warrantyDepositPercentage', 'warrantyDepositMonth', 'warrantyDepositTotal')(formGroup);
      if (a || b) {
        return { ...a, ...b };
      }
      return null;
    };
  }

  static validateWarrantyDepositMonthsLessThanTerm(
    warrantyTypeControlName: string,
    monthControlName: string,
    termControlName: string
  ): ValidatorFn {
    return (formGroup: AbstractControl): { [key: string]: any } | null => {
      const typeControl = formGroup.get(warrantyTypeControlName);
      const monthControl = formGroup.get(monthControlName);
      const termControl = formGroup.get(termControlName);

      if (!typeControl || !monthControl || !termControl) {
        return null;
      }

      const typeValue = typeControl.value;
      const monthValue = monthControl.value;
      const termValue = termControl.value;

      // Only validate when the warranty deposit type is NUMBER_OF_RENTS
      if (typeValue === TaxType.numberOfRents) {
        const bothValuesPresent = monthValue !== null && monthValue !== undefined && termValue !== null && termValue !== undefined;
        const isInvalid = bothValuesPresent && Number(monthValue) >= Number(termValue);

        if (isInvalid) {
          // Set error on the specific control to reflect invalid state on the field
          const existingErrors = monthControl.errors || {};
          monthControl.setErrors({ ...existingErrors, warrantyDepositMonthTooHigh: true });
          return { warrantyDepositMonthTooHigh: true };
        } else {
          // Clear our specific error key if previously set
          if (monthControl.errors && monthControl.errors['warrantyDepositMonthTooHigh']) {
            const { warrantyDepositMonthTooHigh, ...rest } = monthControl.errors;
            monthControl.setErrors(Object.keys(rest).length ? rest : null);
          }
        }
      } else {
        // If the type is not NUMBER_OF_RENTS, ensure our error is cleared
        if (monthControl.errors && monthControl.errors['warrantyDepositMonthTooHigh']) {
          const { warrantyDepositMonthTooHigh, ...rest } = monthControl.errors;
          monthControl.setErrors(Object.keys(rest).length ? rest : null);
        }
      }

      return null;
    };
  }
}
